import React, { useEffect, useMemo, useState } from 'react';
import { apiUrl } from '../lib/api';

const TRACK_OPTIONS = [
  {
    id: 'immigration',
    label: 'Immigration',
    description: 'Moving or working abroad? I will map the paperwork.',
    questions: [
      {
        id: 'origin',
        label: 'Where are you right now?',
        placeholder: 'Current country or city',
        type: 'text',
      },
      {
        id: 'arrived',
        label: 'Already in your destination country?',
        type: 'select',
        options: [
          { value: 'no', label: 'Not yet - still planning the move' },
          { value: 'yes', label: 'Yes - I am already there but need next steps' },
        ],
      },
      {
        id: 'arrivalDate',
        label: 'When did you arrive?',
        type: 'date',
        shouldShow: (answers = {}) => answers.arrived === 'yes',
      },
      {
        id: 'destination',
        label: 'Where do you want to move?',
        placeholder: 'Destination country or city',
        type: 'text',
        shouldShow: (answers = {}) => answers.arrived === 'no',
      },
      {
        id: 'timeline',
        label: 'When would you ideally arrive?',
        type: 'date',
        shouldShow: (answers = {}) => answers.arrived === 'no',
      },
      {
        id: 'documents',
        label: 'What do you already have ready?',
        type: 'inventory',
        options: [
          { id: 'passport', label: 'Valid passport' },
          { id: 'workOffer', label: 'Signed work contract' },
          { id: 'sponsorLetter', label: 'Sponsor / invitation letter' },
          { id: 'fundsProof', label: 'Proof of funds' },
          { id: 'policeCheck', label: 'Police clearance' },
        ],
        otherPlaceholder: 'Add any other documents',
      },
    ],
  },
  {
    id: 'estate',
    label: 'Family Estate',
    description: 'Handling wills, probate, or inheritance logistics.',
    questions: [
      {
        id: 'region',
        label: 'Where is the estate located?',
        placeholder: 'State / Province / Country',
        type: 'text',
      },
      {
        id: 'hasWill',
        label: 'Is there a signed Will available?',
        type: 'select',
        options: [
          { value: 'yes', label: 'Yes, located' },
          { value: 'no', label: 'No or unsure' },
        ],
      },
      {
        id: 'hasExecutor',
        label: 'Has an executor been appointed?',
        type: 'select',
        options: [
          { value: 'yes', label: 'Yes - executor is named and active' },
          { value: 'no', label: 'No - still choosing or unsure' },
        ],
        shouldShow: (answers = {}) => answers.hasWill === 'yes',
      },
      {
        id: 'executorSupport',
        label: 'What support does your executor need?',
        placeholder: 'Share blockers, missing documents, etc.',
        type: 'textarea',
        shouldShow: (answers = {}) => answers.hasExecutor === 'yes',
      },
      {
        id: 'timeline',
        label: 'When do you hope to finalize everything?',
        placeholder: 'e.g., "Within 60 days"',
        type: 'text',
        shouldShow: (answers = {}) => {
          if (answers.hasWill !== 'yes') return true;
          if (!answers.hasExecutor) return false;
          return answers.hasExecutor !== 'yes';
        },
      },
      {
        id: 'assistants',
        label: 'Who is helping you?',
        placeholder: 'Attorneys, siblings, accountants, etc.',
        type: 'textarea',
        shouldShow: (answers = {}) => {
          if (answers.hasWill !== 'yes') return true;
          if (!answers.hasExecutor) return false;
          return answers.hasExecutor !== 'yes';
        },
      },
    ],
  },
];

const TONE_OPTIONS = [
  {
    id: 'supportive',
    label: 'Supportive Guide',
    description: 'Warm, encouraging nudges with real-world tips.',
  },
  {
    id: 'expert',
    label: 'Expert',
    description: 'Direct, detailed guidance with citations where possible.',
  },
  {
    id: 'coach',
    label: 'Focused Coach',
    description: 'Deadline-driven, keeps you accountable to the plan.',
  },
];

const getTrack = (trackId) => TRACK_OPTIONS.find((track) => track.id === trackId);

const DEFAULT_INVENTORY_STATE = { selections: [], other: '' };

const normalizeDateInputValue = (raw) => {
  if (typeof raw !== 'string') {
    return '';
  }
  if (/^\d{4}-\d{2}-\d{2}$/.test(raw)) {
    return raw;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return '';
  }
  return parsed.toISOString().slice(0, 10);
};

const parseInventoryDraft = (raw) => {
  if (!raw) return { ...DEFAULT_INVENTORY_STATE };
  if (typeof raw === 'string') {
    try {
      const parsed = JSON.parse(raw);
      if (parsed && typeof parsed === 'object') {
        return {
          selections: Array.isArray(parsed.selections) ? parsed.selections : [],
          other: typeof parsed.other === 'string' ? parsed.other : '',
        };
      }
    } catch (error) {
      /* ignore */
    }
    return {
      selections: [],
      other: raw,
    };
  }

  if (typeof raw === 'object') {
    return {
      selections: Array.isArray(raw.selections) ? raw.selections : [],
      other: typeof raw.other === 'string' ? raw.other : '',
    };
  }

  return { ...DEFAULT_INVENTORY_STATE };
};

export default function Onboarding({
  user,
  token,
  initialData,
  onProgress,
  onComplete,
}) {
  const [selectedTrack, setSelectedTrack] = useState(initialData?.track || '');
  const [questionIndex, setQuestionIndex] = useState(0);
  const [responses, setResponses] = useState(initialData?.responses || {});
  const [tone, setTone] = useState(initialData?.tone || 'supportive');
  const [inputValue, setInputValue] = useState('');
  const [status, setStatus] = useState({ message: '', error: '' });
  const [submitting, setSubmitting] = useState(false);

  const track = useMemo(() => getTrack(selectedTrack), [selectedTrack]);
  const questions = useMemo(() => {
    if (!track) return [];
    return track.questions.filter((question) => {
      if (typeof question.shouldShow === 'function') {
        return question.shouldShow(responses);
      }
      return true;
    });
  }, [track, responses]);
  const isTriageStep = !selectedTrack;
  const isToneStep = Boolean(selectedTrack) && questionIndex >= questions.length;
  const currentQuestion = !isTriageStep && !isToneStep ? questions[questionIndex] : null;

  useEffect(() => {
    if (!currentQuestion) {
      setInputValue('');
      return;
    }
    if (currentQuestion.type === 'inventory') {
      setInputValue(parseInventoryDraft(responses[currentQuestion.id]));
      return;
    }
    if (currentQuestion.type === 'date') {
      setInputValue(normalizeDateInputValue(responses[currentQuestion.id]));
      return;
    }
    setInputValue(responses[currentQuestion.id] || '');
  }, [currentQuestion, responses]);

  useEffect(() => {
    if (initialData?.track && !selectedTrack) {
      setSelectedTrack(initialData.track);
    }
  }, [initialData, selectedTrack]);

  const persistProgress = (draft) => {
    const payload = {
      track: draft.track ?? selectedTrack,
      responses: draft.responses ?? responses,
      tone: draft.tone ?? tone,
      step: isToneStep ? 'tone' : isTriageStep ? 'triage' : `question-${questionIndex}`,
    };
    onProgress?.(payload);
  };

  const handleInventoryToggle = (optionId) => {
    setInputValue((prev) => {
      const selections = Array.isArray(prev?.selections) ? [...prev.selections] : [];
      const exists = selections.includes(optionId);
      const nextSelections = exists
        ? selections.filter((entry) => entry !== optionId)
        : [...selections, optionId];
      return {
        selections: nextSelections,
        other: typeof prev?.other === 'string' ? prev.other : '',
      };
    });
  };

  const handleInventoryOtherChange = (event) => {
    const { value } = event.target;
    setInputValue((prev) => ({
      selections: Array.isArray(prev?.selections) ? prev.selections : [],
      other: value,
    }));
  };

  const handleTrackSelect = (trackId) => {
    setSelectedTrack(trackId);
    setQuestionIndex(0);
    setResponses({});
    persistProgress({ track: trackId, responses: {} });
  };

  const handleRestartTrack = () => {
    setSelectedTrack('');
    setQuestionIndex(0);
    setResponses({});
    setInputValue('');
    persistProgress({ track: '', responses: {} });
  };

  const handleAnswerSubmit = (event) => {
    event.preventDefault();
    if (!currentQuestion) return;

    let nextValue = '';

    if (currentQuestion.type === 'select') {
      nextValue = inputValue;
      if (!nextValue) {
        setStatus({ message: '', error: 'Please choose an option to continue.' });
        return;
      }
    } else if (currentQuestion.type === 'date') {
      const normalized = normalizeDateInputValue(inputValue);
      if (!normalized) {
        setStatus({ message: '', error: 'Please choose a date to continue.' });
        return;
      }
      nextValue = normalized;
    } else if (currentQuestion.type === 'inventory') {
      const selections = Array.isArray(inputValue?.selections) ? inputValue.selections : [];
      const other = (inputValue?.other || '').trim();
      if (!selections.length && !other) {
        setStatus({ message: '', error: 'Select at least one item or add another detail.' });
        return;
      }
      nextValue = JSON.stringify({ selections, other });
    } else {
      const trimmed = typeof inputValue === 'string' ? inputValue.trim() : '';
      if (!trimmed) {
        setStatus({ message: '', error: 'Please enter a response to continue.' });
        return;
      }
      nextValue = trimmed;
    }

    const updated = {
      ...responses,
      [currentQuestion.id]: nextValue,
    };

    if (currentQuestion.id === 'arrived') {
      if (nextValue === 'yes') {
        delete updated.destination;
        delete updated.timeline;
      } else {
        delete updated.arrivalDate;
      }
    }

    if (currentQuestion.id === 'hasWill') {
      if (nextValue !== 'yes') {
        delete updated.hasExecutor;
        delete updated.executorSupport;
      } else {
        delete updated.timeline;
        delete updated.assistants;
      }
    }

    if (currentQuestion.id === 'hasExecutor') {
      if (nextValue === 'yes') {
        delete updated.timeline;
        delete updated.assistants;
      } else {
        delete updated.executorSupport;
      }
    }
    setResponses(updated);
    setQuestionIndex((prev) => prev + 1);
    setStatus({ message: '', error: '' });
    persistProgress({ responses: updated });
  };

  const handleBack = () => {
    if (isToneStep) {
      setQuestionIndex(Math.max(questions.length - 1, 0));
      return;
    }
    if (questionIndex > 0) {
      setQuestionIndex((prev) => prev - 1);
      return;
    }
    handleRestartTrack();
  };

  const handleToneSubmit = async () => {
    if (!selectedTrack || !questions.length) {
      setStatus({ message: '', error: 'Please select a track to continue.' });
      return;
    }

    const payload = {
      track: selectedTrack,
      responses,
      tone,
      completedAt: new Date().toISOString(),
    };

    setSubmitting(true);
    setStatus({ message: '', error: '' });

    try {
      if (token) {
        const response = await fetch(apiUrl('/api/intake'), {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Bearer ${token}`,
          },
          body: JSON.stringify(payload),
        });

        if (!response.ok) {
          throw new Error('Request failed');
        }

        const result = await response.json().catch(() => null);
        if (result?.plan) {
          payload.plan = result.plan;
        }
      }
    } catch (error) {
      setStatus({ message: '', error: 'Unable to sync intake details right now.' });
      setSubmitting(false);
      return;
    }

    persistProgress(payload);
    onComplete?.(payload);
    setStatus({ message: 'Onboarding complete. Welcome aboard.', error: '' });
    setSubmitting(false);
  };

  return (
    <section className="onboarding-shell">
      <div className="onboarding-panel">
        <header className="onboarding-header">
          <div>
            <p className="eyebrow">Onboarding</p>
            <h1>Let's map your path, {user?.name?.split(' ')[0] || 'friend'}.</h1>
            <p className="helper">
              Answer a few focused questions so I can generate the first playbook for you.
            </p>
          </div>
        </header>

        {isTriageStep && (
          <div className="triage-grid">
            {TRACK_OPTIONS.map((option) => (
              <button
                key={option.id}
                type="button"
                className="triage-card"
                onClick={() => handleTrackSelect(option.id)}
              >
                <span className="triage-title">{option.label}</span>
                <p>{option.description}</p>
              </button>
            ))}
          </div>
        )}

        {!isTriageStep && !isToneStep && currentQuestion && (
          <form className="question-card" onSubmit={handleAnswerSubmit}>
            <p className="eyebrow">{track?.label}</p>
            <h2>{currentQuestion.label}</h2>
            {currentQuestion.description && <p className="helper">{currentQuestion.description}</p>}

            {currentQuestion.type === 'select' ? (
              <select
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                required
              >
                <option value="" disabled>
                  Select an option
                </option>
                {currentQuestion.options?.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </select>
            ) : currentQuestion.type === 'inventory' ? (
              <div className="inventory-group">
                <div className="inventory-options">
                  {currentQuestion.options?.map((option) => {
                    const active = Array.isArray(inputValue?.selections)
                      ? inputValue.selections.includes(option.id)
                      : false;
                    return (
                      <button
                        key={option.id}
                        type="button"
                        className={`inventory-toggle ${active ? 'active' : ''}`}
                        onClick={() => handleInventoryToggle(option.id)}
                      >
                        <span className="toggle-indicator" />
                        {option.label}
                      </button>
                    );
                  })}
                </div>
                <label className="inventory-other">
                  <span>Other</span>
                  <input
                    type="text"
                    value={inputValue?.other || ''}
                    placeholder={currentQuestion.otherPlaceholder || 'Add more details'}
                    onChange={handleInventoryOtherChange}
                  />
                </label>
              </div>
            ) : currentQuestion.type === 'textarea' ? (
              <textarea
                rows="4"
                value={inputValue}
                placeholder={currentQuestion.placeholder}
                onChange={(event) => setInputValue(event.target.value)}
                required
              />
            ) : currentQuestion.type === 'date' ? (
              <input
                type="date"
                value={inputValue}
                onChange={(event) => setInputValue(event.target.value)}
                required
              />
            ) : (
              <input
                type="text"
                value={inputValue}
                placeholder={currentQuestion.placeholder}
                onChange={(event) => setInputValue(event.target.value)}
                required
              />
            )}

            <div className="question-actions">
              <button type="button" className="ghost" onClick={handleBack}>
                Back
              </button>
              <button type="submit" className="primary">
                Next
              </button>
            </div>
          </form>
        )}

        {isToneStep && (
          <div className="tone-card">
            <p className="eyebrow">Tone</p>
            <h2>How would you like Pathfinder AI to guide you?</h2>
            <div className="tone-options">
              {TONE_OPTIONS.map((option) => (
                <label key={option.id} className={`tone-option ${tone === option.id ? 'active' : ''}`}>
                  <input
                    type="radio"
                    name="tone"
                    value={option.id}
                    checked={tone === option.id}
                    onChange={() => {
                      setTone(option.id);
                      persistProgress({ tone: option.id });
                    }}
                  />
                  <div>
                    <span>{option.label}</span>
                    <p>{option.description}</p>
                  </div>
                </label>
              ))}
            </div>
            <div className="question-actions">
              <button type="button" className="ghost" onClick={handleBack}>
                Back
              </button>
              <button
                type="button"
                className="primary"
                onClick={handleToneSubmit}
                disabled={submitting}
              >
                {submitting ? 'Saving...' : 'Generate my plan'}
              </button>
            </div>
          </div>
        )}

        {status.message && <p className="status-line success onboarding-status">{status.message}</p>}
        {status.error && <p className="status-line error onboarding-status">{status.error}</p>}
      </div>
    </section>
  );
}
